import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { profileAPI, swipeAPI } from '../services/api';
import { BottomNav } from '../components/BottomNav';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Camera, Settings, LogOut, Edit2, Shield, Crown, MapPin, Languages, Heart, Save, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, profile, logout, updateProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [swipeStatus, setSwipeStatus] = useState(null);
  const [formData, setFormData] = useState({
    bio: '',
    location_country: '',
    location_city: '',
    occupation: '',
    languages: [],
    religion: '',
    intent: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        bio: profile.bio || '',
        location_country: profile.location_country || '',
        location_city: profile.location_city || '',
        occupation: profile.occupation || '',
        languages: profile.languages || [],
        religion: profile.religion || '',
        intent: profile.intent || ''
      });
    }
    loadSwipeStatus();
  }, [profile]);

  const loadSwipeStatus = async () => {
    try {
      const response = await swipeAPI.getStatus();
      setSwipeStatus(response.data);
    } catch (error) {
      console.error('Failed to load swipe status:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await profileAPI.update(formData);
      updateProfile(response.data.profile);
      setEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const mainPhoto = profile?.photos?.[0] 
    ? (profile.photos[0].startsWith('/api') 
        ? `${process.env.REACT_APP_BACKEND_URL}${profile.photos[0]}`
        : profile.photos[0])
    : 'https://images.unsplash.com/photo-1745572587190-962fe038f073?crop=entropy&cs=srgb&fm=jpg&q=85';

  return (
    <div className="min-h-screen bg-dark pb-24" data-testid="profile-page">
      {/* Header */}
      <div className="relative h-64 bg-gradient-to-b from-gold/20 to-transparent">
        {/* Background blur */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20 blur-xl"
          style={{ backgroundImage: `url(${mainPhoto})` }}
        />
        
        {/* Settings button */}
        <div className="absolute top-4 right-4 flex gap-2">
          <button
            onClick={() => navigate('/settings')}
            className="p-3 rounded-full bg-dark/50 backdrop-blur-md text-white hover:bg-dark/70 transition-colors"
            data-testid="settings-btn"
          >
            <Settings className="w-5 h-5" />
          </button>
          <button
            onClick={() => setEditing(!editing)}
            className="p-3 rounded-full bg-dark/50 backdrop-blur-md text-white hover:bg-dark/70 transition-colors"
            data-testid="edit-profile-btn"
          >
            <Edit2 className="w-5 h-5" />
          </button>
          <button
            onClick={handleLogout}
            className="p-3 rounded-full bg-dark/50 backdrop-blur-md text-white hover:bg-red-500/50 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* Profile photo */}
        <div className="absolute -bottom-16 left-1/2 -translate-x-1/2">
          <div className="relative">
            <div className="w-32 h-32 rounded-full border-4 border-dark overflow-hidden shadow-xl">
              <img
                src={mainPhoto}
                alt={user?.name}
                className="w-full h-full object-cover"
              />
            </div>
            {user?.is_verified && (
              <div className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                <Shield className="w-4 h-4 text-black" fill="black" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 pt-20">
        {/* Name & badges */}
        <div className="text-center mb-6">
          <h1 className="font-heading text-2xl text-white">
            {user?.name}
            {profile?.age && <span className="text-xl text-gray-400 ml-2">{profile.age}</span>}
          </h1>
          <div className="flex items-center justify-center gap-2 mt-2">
            {user?.is_premium && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gold/20 text-gold text-xs font-medium rounded-full">
                <Crown className="w-3 h-3" />
                {user.premium_tier?.charAt(0).toUpperCase() + user.premium_tier?.slice(1)}
              </span>
            )}
            {user?.is_verified && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                <Shield className="w-3 h-3" />
                Verified
              </span>
            )}
          </div>
        </div>

        {/* Swipe Status Indicator */}
        {swipeStatus && !swipeStatus.is_unlimited && (
          <div className="mb-6 p-4 bg-dark-paper border border-white/5 rounded-2xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400 font-body">Daily Swipes</span>
              <span className="text-sm text-gold font-body font-semibold">
                {swipeStatus.swipes_remaining}/{swipeStatus.daily_limit} left
              </span>
            </div>
            <div className="h-2 bg-dark-elevated rounded-full overflow-hidden">
              <div 
                className="h-full bg-gold rounded-full transition-all"
                style={{ width: `${(swipeStatus.swipes_remaining / swipeStatus.daily_limit) * 100}%` }}
              />
            </div>
            {swipeStatus.swipes_remaining <= 5 && (
              <p className="text-xs text-gray-500 mt-2">
                Resets at midnight UTC
              </p>
            )}
          </div>
        )}

        {/* Profile completion */}
        {profile?.profile_completion < 100 && (
          <div className="mb-6 p-4 bg-dark-paper border border-white/5 rounded-2xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400 font-body">Profile completion</span>
              <span className="text-sm text-gold font-body">{profile?.profile_completion || 0}%</span>
            </div>
            <div className="h-2 bg-dark-elevated rounded-full overflow-hidden">
              <div 
                className="h-full bg-gold rounded-full"
                style={{ width: `${profile?.profile_completion || 0}%` }}
              />
            </div>
          </div>
        )}

        {editing ? (
          /* Edit form */
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm text-gray-300 mb-2 font-body">Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                rows={3}
                className="w-full bg-dark-elevated border border-white/10 text-white rounded-xl p-4 placeholder:text-gray-500 focus:outline-none focus:border-gold/50 font-body"
                data-testid="edit-bio"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2 font-body">Country</label>
                <Input
                  value={formData.location_country}
                  onChange={(e) => setFormData(prev => ({ ...prev, location_country: e.target.value }))}
                  className="bg-dark-elevated border-white/10 text-white h-12 rounded-xl"
                  data-testid="edit-country"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2 font-body">City</label>
                <Input
                  value={formData.location_city}
                  onChange={(e) => setFormData(prev => ({ ...prev, location_city: e.target.value }))}
                  className="bg-dark-elevated border-white/10 text-white h-12 rounded-xl"
                  data-testid="edit-city"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-2 font-body">Occupation</label>
              <Input
                value={formData.occupation}
                onChange={(e) => setFormData(prev => ({ ...prev, occupation: e.target.value }))}
                className="bg-dark-elevated border-white/10 text-white h-12 rounded-xl"
                data-testid="edit-occupation"
              />
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full h-12 btn-gold rounded-full font-body font-semibold"
              data-testid="save-profile-btn"
            >
              {saving ? 'Saving...' : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </motion.div>
        ) : (
          /* View mode */
          <div className="space-y-4">
            {/* Bio */}
            {profile?.bio && (
              <div className="p-4 bg-dark-paper border border-white/5 rounded-2xl">
                <p className="text-white font-body">{profile.bio}</p>
              </div>
            )}

            {/* Info grid */}
            <div className="grid grid-cols-2 gap-3">
              {profile?.location_country && (
                <div className="p-4 bg-dark-paper border border-white/5 rounded-xl">
                  <MapPin className="w-5 h-5 text-gold mb-2" />
                  <p className="text-sm text-gray-400 font-body">Location</p>
                  <p className="text-white font-body">
                    {profile.location_city && `${profile.location_city}, `}
                    {profile.location_country}
                  </p>
                </div>
              )}

              {profile?.languages?.length > 0 && (
                <div className="p-4 bg-dark-paper border border-white/5 rounded-xl">
                  <Languages className="w-5 h-5 text-gold mb-2" />
                  <p className="text-sm text-gray-400 font-body">Languages</p>
                  <p className="text-white font-body">{profile.languages.slice(0, 2).join(', ')}</p>
                </div>
              )}

              {profile?.intent && (
                <div className="p-4 bg-dark-paper border border-white/5 rounded-xl">
                  <Heart className="w-5 h-5 text-gold mb-2" />
                  <p className="text-sm text-gray-400 font-body">Looking for</p>
                  <p className="text-white font-body">{profile.intent.replace('_', ' ')}</p>
                </div>
              )}

              {profile?.occupation && (
                <div className="p-4 bg-dark-paper border border-white/5 rounded-xl">
                  <Settings className="w-5 h-5 text-gold mb-2" />
                  <p className="text-sm text-gray-400 font-body">Occupation</p>
                  <p className="text-white font-body">{profile.occupation}</p>
                </div>
              )}
            </div>

            {/* Settings Link */}
            <button
              onClick={() => navigate('/settings')}
              className="w-full p-4 bg-dark-paper border border-white/5 rounded-2xl flex items-center gap-4"
              data-testid="settings-link"
            >
              <Settings className="w-6 h-6 text-gray-400" />
              <div className="text-left flex-1">
                <p className="text-white font-body font-semibold">Account Settings</p>
                <p className="text-sm text-gray-400">Privacy, membership, photos & more</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>

            {/* Upgrade CTA */}
            {!user?.is_premium && (
              <button
                onClick={() => navigate('/premium')}
                className="w-full p-4 bg-gradient-to-r from-gold/20 to-gold/10 border border-gold/30 rounded-2xl flex items-center gap-4"
                data-testid="upgrade-cta"
              >
                <Crown className="w-10 h-10 text-gold" />
                <div className="text-left flex-1">
                  <p className="text-white font-body font-semibold">Upgrade to Premium</p>
                  <p className="text-sm text-gray-400">Unlock unlimited swipes & more</p>
                </div>
              </button>
            )}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
